/**
 * The cost plan as a tree (AP-86): which groups show at a chosen depth, which
 * are folded, and which carry anything at all. Pure, for the table in CostsPage.
 */

export interface TreeNode {
  code: string;
  parent: string | null;
  /** 0 for 100 … 800. */
  depth: number;
}

/** Depth choice: 1, 2, 3 levels, or everything. */
export type DepthChoice = 1 | 2 | 3 | 'all';

export function childrenOf(nodes: readonly TreeNode[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const n of nodes) if (n.parent !== null) out.set(n.parent, [...(out.get(n.parent) ?? []), n.code]);
  return out;
}

/** The groups that are unfolded when a depth is chosen: all above that depth. */
export function expandedForDepth(nodes: readonly TreeNode[], depth: DepthChoice): Set<string> {
  const kids = childrenOf(nodes);
  return new Set(nodes.filter((n) => kids.has(n.code) && (depth === 'all' || n.depth < depth - 1)).map((n) => n.code));
}

/**
 * The groups to show, in order: every ancestor unfolded, and - when `used` is
 * given - only those that carry something themselves or below.
 */
export function visibleCodes(nodes: readonly TreeNode[], expanded: ReadonlySet<string>, used?: (code: string) => boolean): string[] {
  const byCode = new Map(nodes.map((n) => [n.code, n]));
  const kids = childrenOf(nodes);
  const usedBelow = new Map<string, boolean>();
  const carries = (code: string): boolean => {
    const known = usedBelow.get(code);
    if (known !== undefined) return known;
    const v = (used?.(code) ?? true) || (kids.get(code) ?? []).some(carries);
    usedBelow.set(code, v);
    return v;
  };
  const open = (code: string): boolean => {
    for (let p = byCode.get(code)?.parent ?? null; p !== null; p = byCode.get(p)?.parent ?? null) if (!expanded.has(p)) return false;
    return true;
  };
  return nodes.filter((n) => open(n.code) && (!used || carries(n.code))).map((n) => n.code);
}
